import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';

const container = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.12, delayChildren: 0.2 },
  },
};

const item = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const HeroSection = () => {
  const stats = [
    { value: '50+', label: 'Products Coming' },
    { value: '10+', label: 'Local Artisans' },
    { value: '100%', label: 'Authentic' },
  ];

  return (
    <section className="relative overflow-hidden min-h-[600px] md:min-h-[85vh] flex items-center">
      {/* Video Background */}
      <div className="absolute inset-0 w-full h-full">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src='https://res.cloudinary.com/drariarqq/video/upload/v1773571081/hero-background_yqs8s5.mp4' type="video/mp4" />
          Your browser does not support the video tag.
        </video>

        {/* Warm overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-br from-cocoa-950/85 via-cocoa-900/70 to-clay-900/75"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-cream-50 via-transparent to-cocoa-950/40"></div>
      </div>

      {/* Ambient warm blobs */}
      <div className="absolute inset-0 opacity-20 mix-blend-overlay">
        <div className="absolute top-1/4 left-10 w-80 h-80 bg-saffron-400 rounded-full mix-blend-multiply filter blur-3xl animate-pulse-soft"></div>
        <div className="absolute bottom-1/4 right-10 w-80 h-80 bg-clay-400 rounded-full mix-blend-multiply filter blur-3xl animate-pulse-soft"></div>
      </div>

      {/* Content */}
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="relative container mx-auto px-4 z-10"
      >
        <div className="max-w-4xl mx-auto text-center">
          {/* Eyebrow Badge */}
          <motion.div
            variants={item}
            className="inline-flex items-center gap-2 px-5 py-2 bg-white/10 backdrop-blur-md text-saffron-200 border border-white/25 rounded-full mb-8"
          >
            <Sparkles className="w-4 h-4" />
            <span className="font-semibold text-sm uppercase tracking-wider">
              Authentic Bengali Handicrafts
            </span>
          </motion.div>

          {/* Main Heading */}
          <motion.h1
            variants={item}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-[1.1] text-white"
          >
            Welcome to{' '}
            <span className="font-bengali bg-gradient-to-r from-saffron-300 via-amber-200 to-rose-300 bg-clip-text text-transparent">
              তন্তিকা
            </span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            variants={item}
            className="text-lg md:text-xl text-cream-100/90 mb-10 max-w-3xl mx-auto"
          >
            Discover authentic Bengali crafts and artisanal products.
            We're building a marketplace that celebrates traditional craftsmanship
            and supports local artisans.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            variants={item}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link
              to="/shop"
              className="group bg-gradient-to-r from-saffron-500 to-clay-500 text-white px-8 py-4 rounded-full font-semibold text-lg shadow-warm-lg hover:-translate-y-1 hover:shadow-xl transition-all duration-300 flex items-center justify-center"
            >
              <span>Browse Products</span>
              <ArrowRight className="ml-3 w-5 h-5 group-hover:translate-x-2 transition-transform" />
            </Link>

            <Link
              to="/register"
              className="group border-2 border-white/70 text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white/10 transition-all duration-300 flex items-center justify-center backdrop-blur-sm"
            >
              <span>Join Our Community</span>
            </Link>
          </motion.div>

          {/* Stats */}
          <motion.div
            variants={item}
            className="mt-16 flex flex-wrap justify-center gap-8 md:gap-14"
          >
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-saffron-300 to-rose-300 bg-clip-text text-transparent">
                  {stat.value}
                </div>
                <div className="text-cream-100/85 font-medium mt-1">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
